import React, { useState, useEffect } from 'react';
import { Shield, Key, Users, RefreshCw, UserCircle, Settings, ChevronDown, Lock, Globe, FileText, Repeat } from 'lucide-react';
import './projectShowcase.css';

import cybersecurity from './cryptoss-img.jpg';
import samlSsoImg from './Saml-sso-img.webp';
import Iam from './Iam-img.webp';
import moodle from './moodle-img.jpg';
import auth from './auth-img.png';

const ProjectShowcase = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const images = [cybersecurity, samlSsoImg, Iam, moodle, auth];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 2000);

    return () => clearInterval(interval);
  }, [images]);

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  const projects = [
    {
      title:"SSO Plugin for Wordpress",
      link:'https://plugins.miniorange.com/wordpress-single-sign-on-sso',
      icon: <Lock size={16} />
    },
    {
      title: "SSO Plugin for Moodle",
      link: 'https://plugins.miniorange.com/moodle-single-sign-on-sso',
      icon: <Globe size={16} />
    },
    {
      title: "Page Restriction for WordPress",
      link: 'https://www.miniorange.com/blog/wordpress-page-post-restriction-addon/',
      icon: <FileText size={16} />
    },
    {
      title: "SCIM Protocol for WordPress",
      link: 'https://plugins.miniorange.com/wordpress-user-provisioning',
      icon: <Repeat size={16} />
    }
  ];

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const features = [
    { icon: <Shield size={18} />, text: "SAML 2.0 SSO Integration" },
    { icon: <Key size={18} />, text: "IAM & CIAM" },
    { icon: <Users size={18} />, text: "User Authentication & Authorization" },
    { icon: <RefreshCw size={18} />, text: "Real-time Data Sync" },
    { icon: <UserCircle size={18} />, text: "Single Membership ID" },
    { icon: <Settings size={18} />, text: "SCIM Management" },
  ];

  const technologies = [
    { icon: "🐘", text: "PHP" },
    { icon: "🔌", text: "WordPress/Moodle Plugins" },
    { icon: "🌐", text: "JavaScript, HTML, CSS" },
    { icon: "🔐", text: "SAML, OAuth" },
    { icon: "🔗", text: "Rest API, API Integration" },
    { icon: "🛡️", text: "Security Protocols" },
  ];

  return (
    <div className="project-showcase">
      <div className="showcase-content">
        <div className="image-carousel">
          <img
            src={images[currentImageIndex]}
            alt={`Cybersecurity solution ${currentImageIndex + 1}`}
          />
          <div className="dots-container">
            {images.map((_, index) => (
              <span
                key={index}
                className={`dot ${currentImageIndex === index ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </div>
        <div className="project-details">
          <h2>🛡️ Cybersecurity Solutions</h2>
          <p>
          I worked on cybersecurity products at MiniOrange to enhance authentication and authorization processes across various platforms. This category includes projects like the SAML SSO Plugin for WordPress, the Page Restriction Plugin for WordPress, and the SAML SSO Plugin for Moodle, along with SCIM Authentication. I focused on SSO protocols, implementing SAML authentication and authorization, managing SP and IdP transfers, and ensuring secure access and exchange of user data.
          </p>
          <div className="features-technologies">
            <div className="column">
              <h3>Key Features</h3>
              {features.map((item, index) => (
                <div key={index} className="item">
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            <div className="column">
              <h3>Technologies Used</h3>
              {technologies.map((item, index) => (
                <div key={index} className="item">
                  <span className="tech-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="project-dropdown">
            <button className="dropdown-button" onClick={toggleDropdown}>
              Explore Cybersecurity Products I’ve Worked On <ChevronDown size={16} />
            </button>
            <div className={`dropdown-content ${dropdownOpen ? 'show' : ''}`}>
              {projects.map((project, index) => (
                <a
                  key={index}
                  href={project.link}
                  className="project-link"
                  target="_blank"
                  rel="noopener noreferrer" // Ensures security by preventing the new page from accessing window.opener
                >
                  {project.icon}
                  <span>{project.title}</span>
                </a>
              ))}
            </div>

          </div>
          <p className="dropdown-note">Click on a solution to view detailed project information and implementation guides.</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectShowcase;
